/* eslint-disable max-lines */
import React, { useEffect, Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Tooltip from '@material-ui/core/Tooltip';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ChatDisconnectedIcon from '@material-ui/icons/Announcement';
import ChatIcon from '@material-ui/icons/Chat';
import ForumIcon from '@material-ui/icons/Forum';
import SettingsIcon from '@material-ui/icons/Settings';

import AccessLevelService, { userModerator } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import SessionStorageService from 'services/SessionStorageService';
import ChatStore from 'stores/ChatStore';
import GamesStore from 'stores/GamesStore';
import GiphyStore from 'stores/GiphyStore';
import GroupShowStore from 'stores/GroupShowStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { CHAT_AVATAR_URL } from 'containers/Chat/Api';
import ChatExpandButton from 'components/ChatExpandButton/ChatExpandButton';
import ChatSettingsV2 from 'components/ChatSettingsV2/ChatSettingsV2';
import { SENT_CHAT_MESSAGE_STATUSES } from 'enum/helpers';
import { NOTIFICATION_TYPE } from 'enum/messages';
import {
  MINUTE_AS_MILLISECONDS,
  SECOND_AS_MILLISECONDS,
} from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
} from 'enum/videoPlayer';
import { isMyProfileHelperFunc, noOp } from 'utils/helpers';
import useTimeout from 'hooks/useTimeout';
import ThemeService from 'themes/ThemeService';

import groupShowTicketIcon from '../../../../../../../images/gs-icon.png';
import DesktopChatV2 from '../DesktopChatV2/DesktopChatV2';
import GamesTab from '../GamesTab/GamesTab';
import MultiplePrivateChat from '../MultiplePrivateChat/MultiplePrivateChat';
import UserListTab from '../UserListTab/UserListTab';

import TabPanel from './TabPanel';
import styles from './ChatTabs.css';


const DEFAULT_CAMPAIGN_MESSAGE_TIMEOUT = 3 * MINUTE_AS_MILLISECONDS;
const SHOW_DISCONNECTED_ICON_DELAY = 5 * SECOND_AS_MILLISECONDS;

function a11yProps(index) {
  return {
    id: `chat-tabs-${index}`,
    'aria-controls': `chat-tabsPanel-${index}`,
  };
}

// eslint-disable-next-line complexity
function ChatTabs(props) {
  const {
    appActions: { setShowRegistrationModal },
    embedProfileV2,
    handleGameTab,
    handlePrivateChat,
    handleUserList,
    history,
    isRegisterGoldOverlayVisible,
    match,
    paymentActions: { toggleRegisterGoldOverlay },
    user,
  } = props;

  const {
    chatInputErrorMessage,
    hasPrivateUnreadMessages,
    hasUnreadGsMessages,
    hasUnreadMessages,
    hasUnreadUser2UserMessages,
    isPrivateMessageOpen,
    profileChatTabValue,
    setProfileChatTabValue,
    toggleGsUnreadMessages,
    togglePrivateMessages,
    toggleUnreadMessages,
  } = ChatStore;

  const {
    gameName,
    gamePaused,
    rollTheDiceActive,
    spinTheWheelActive,
    tokenKenoActive,
  } = GamesStore;

  const {
    broadcastStatus,
    isPlayerBroadcastMode,
    performerDetails,
    privateShowEnded,
    profileName,
    realityShow,
  } = ProfileStore;

  const {
    closePopper,
    isGifsWrapperVisible,
  } = GiphyStore;

  const {
    externalViewersCount = 0,
    newShowsState,
    totalGuestUsers,
    totalLoggedInUsers,
    totalUsers: cam4ViewersCount = 0,
  } = FirebaseStore;

  const isRealityShow = Boolean(realityShow);

  const { user: { accessLevel }, hasCF } = UserStore;

  const [isRecentSpendingTierLegendOpen, setIsRecentSpendingTierLegendOpen] = useState(false);

  const onToggleRecentSpendingTierLegend = useCallback(() => {
    setIsRecentSpendingTierLegendOpen(!isRecentSpendingTierLegendOpen);
  }, [isRecentSpendingTierLegendOpen]);

  const isGenderNotAllowedToChat = chatInputErrorMessage === SENT_CHAT_MESSAGE_STATUSES.GENDER_NOT_ALLOWED_TO_CHAT;

  const isSfw = user.user.getIn(['user', 'sfw']);
  const showPublicChatCF = UserStore.hasCF(allUserGroups.PROFILES_V2_SPECIAL_SHOW_PUBLIC_CHAT);
  const showGSchat =
    showPublicChatCF &&
    GroupShowStore.userHaveTicket(user.username) &&
    broadcastStatus === BROADCAST_STATUS.INSIDE_GS &&
    newShowsState === NEW_SHOWS_STATE.INSIDE_GS;
  const isGroupShowChat =
    (newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
      newShowsState === NEW_SHOWS_STATE.BONUS_GS ||
      newShowsState === NEW_SHOWS_STATE.PAUSED_GS) &&
    GroupShowStore.userHaveTicket(user.username) &&
    !showPublicChatCF;
  const privateMessageIndex = showGSchat ? 2 : 1;
  const isMinUserModerator = AccessLevelService.isMin(userModerator, accessLevel);
  const shouldShowPrivateChatInEmbedProfile =
    !embedProfileV2 ||
    (embedProfileV2 && newShowsState === NEW_SHOWS_STATE.INSIDE_PS) ||
    Boolean(SessionStorageService.get('wasUserInPrivateShow'));
  const isBroadcasterAsViewer = hasCF(allUserGroups.BROADCASTER_AS_VIEWER) && isMyProfileHelperFunc(user, profileName);

  const handleChange = useCallback((_event, newValue) => {
    setIsRecentSpendingTierLegendOpen(false);

    if (isGifsWrapperVisible) {
      closePopper();
    }

    if (isPrivateMessageOpen && newValue !== privateMessageIndex) {
      togglePrivateMessages(false);
    }

    if (newValue === 1) {
      if (showGSchat) {
        toggleGsUnreadMessages(false);
        setProfileChatTabValue(newValue);
      } else {
        handlePrivateChat(newValue);
      }
    } else if (newValue === 2 && showGSchat) {
      handlePrivateChat(newValue);
    } else if (newValue === 0) {
      toggleUnreadMessages(false);
      setProfileChatTabValue(newValue);
    } else if (newValue === 3) {
      handleUserList(newValue);
    } else if (newValue === 4 && gameName) {
      if (gamePaused) {
        return;
      }
      handleGameTab(newValue);
    } else {
      setProfileChatTabValue(newValue);
    }
  }, [
    closePopper,
    gameName,
    gamePaused,
    handleGameTab,
    handlePrivateChat,
    handleUserList,
    isGifsWrapperVisible,
    isPrivateMessageOpen,
    privateMessageIndex,
    setProfileChatTabValue,
    showGSchat,
    toggleGsUnreadMessages,
    togglePrivateMessages,
    toggleUnreadMessages,
  ]);

  useEffect(() => {
    let timer = null;
    const theme = ThemeService.theme.chatOffer;
    if (theme && !ChatStore.campaignMsg) {
      timer = setTimeout(() => {
        ChatStore.addMessage({
          type: NOTIFICATION_TYPE.CAMPAIGN_CTA,
          messageKey: 'XClL1L1Y',
        });
      }, theme.delay || DEFAULT_CAMPAIGN_MESSAGE_TIMEOUT);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (privateShowEnded) {
      setProfileChatTabValue(0);
      togglePrivateMessages(false);
    }
  }, [
    privateShowEnded,
    setProfileChatTabValue,
    togglePrivateMessages,
  ]);

  useEffect(() => {
    if (newShowsState === NEW_SHOWS_STATE.INSIDE_GS) {
      setProfileChatTabValue(0);
    }
  }, [newShowsState, setProfileChatTabValue]);

  useEffect(() => {
    if (showGSchat) {
      setProfileChatTabValue(1);
    }
  }, [showGSchat, setProfileChatTabValue]);

  const gamesTabClasses = classNames({
    [styles.GamesTab]: true,
    [styles.WheelBg]: spinTheWheelActive,
    [styles.KenoBg]: tokenKenoActive,
    [styles.DiceBg]: rollTheDiceActive,
    [styles.disabled]: gamePaused,
  });

  const PMTabClasses = classNames({
    [styles.Tab]: true,
    [styles.unreadPrivateMessage]: hasPrivateUnreadMessages,
  });
  const PMETabClasses = classNames({
    [styles.Tab]: true,
    [styles.unreadUser2UserMessages]: hasUnreadUser2UserMessages,
  });

  const imgClass = classNames({ [styles.sfwImg]: isSfw });

  const performerImg = (
    <div className={styles.performerImg}>
      <img alt="avatar" className={imgClass} src={CHAT_AVATAR_URL.replace('{username}', match.params.user)} />
      {isPrivateMessageOpen && <span>{performerDetails.username}</span>}
    </div>
  );

  const { isChatConnected } = ProfileStore;
  const [showDisconnectedIcon, setShowDisconnectedIcon] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useTimeout(() => {
    if (!isChatConnected && !isGenderNotAllowedToChat) {
      setShowDisconnectedIcon(true);
    }
  }, SHOW_DISCONNECTED_ICON_DELAY);

  useEffect(() => {
    if (isChatConnected) {
      setShowDisconnectedIcon(false);
    }
  }, [isChatConnected]);

  const showTooltipFn = useCallback(() => {
    setShowTooltip(true);
  }, []);

  const hideTooltipFn = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const determineMultiplePrivateChatIndex = () => {
    if (showGSchat) {
      if (isBroadcasterAsViewer) {
        return 2;
      }

      return 3;
    }
    if (isBroadcasterAsViewer) {
      return 1;
    }

    return 2;
  };

  const determineUserListChatIndex = () => {
    if (showGSchat) {
      if (isBroadcasterAsViewer) {
        return 3;
      }

      return 4;
    }
    if (isBroadcasterAsViewer) {
      return 2;
    }

    return 3;
  };

  const determineGameTabIndex = () => {
    if (embedProfileV2) {
      if (SessionStorageService.get('wasUserInPrivateShow')) {
        if (showGSchat) {
          return 3;
        }

        return 2;
      }
      if (showGSchat) {
        return 2;
      }

      return 1;
    }
    if (showGSchat) {
      return 5;
    }

    return 4;
  };

  const determineChatSettingsChatIndex = () => {
    const settingsIndex = gameName ? 5 : 4;
    if (showGSchat) {
      if (isBroadcasterAsViewer) {
        return 4;
      }

      return settingsIndex + 1;
    }
    if (isBroadcasterAsViewer) {

      return 3;
    }

    return settingsIndex;
  };

  let totalUsers = cam4ViewersCount;
  if (isRealityShow) {
    totalUsers += externalViewersCount;
  }

  return (
    <div className={styles.ChatTabs}>
      {isPlayerBroadcastMode && (
        <ChatExpandButton customClass={styles.expand} />
      )}
      <Tooltip
        PopperProps={{ disablePortal: true }}
        classes={{
          tooltip: styles.tooltipText,
          popper: styles.popper,
        }}
        open={showTooltip}
        placement="bottom-start"
        title={translate('v2profile.chat.disconected.tooltip')}
      >
        <div />
      </Tooltip>
      <AppBar position="static">
        <Tabs
          aria-label="Broadcasting Chat Tabs"
          className={styles.TabsContainer}
          classes={{ indicator: styles.Indicator }}
          value={profileChatTabValue}
          onChange={handleChange}
        >
          <Tab
            className={styles.Tab}
            icon={
              <Fragment>
                {showDisconnectedIcon ? (
                  <div
                    className={styles.chatDisconnected}
                    onMouseLeave={hideTooltipFn}
                    onMouseOver={showTooltipFn}
                  >
                    <ChatDisconnectedIcon />
                  </div>
                ) : (
                  <div className={styles.iconWrapper}>
                    <ChatIcon />
                    {hasUnreadMessages && (
                      <span className={styles.unreadMsg} />
                    )}
                  </div>
                )}
              </Fragment>
            }
            {...a11yProps(0)}
          />
          {showGSchat && (
            <Tab
              className={styles.Tab}
              icon={
                <Fragment>
                  <img
                    alt="group show"
                    className={styles.groupShowIcon}
                    src={groupShowTicketIcon}
                  />
                  {hasUnreadGsMessages && (
                    <span className={styles.unreadMsg} />
                  )}
                </Fragment>
              }
              {...a11yProps('group-show-chat')}
            />
          )}
          {shouldShowPrivateChatInEmbedProfile && !isBroadcasterAsViewer && (
            <Tab
              className={PMTabClasses}
              icon={
                <Fragment>
                  {performerImg}
                  {hasPrivateUnreadMessages && (
                    <span className={styles.unreadMsg} />
                  )}
                </Fragment>
              }
              {...a11yProps(1)}
            />
          )}
          {!embedProfileV2 && (
            <Tab
              className={PMETabClasses}
              icon={
                <div className={styles.iconWrapper}>
                  <ForumIcon />
                  {hasUnreadUser2UserMessages && (
                    <span className={styles.unreadMsg} />
                  )}
                </div>
              }
              {...a11yProps(2)}
            />
          )}
          {!embedProfileV2 && (
            <Tab
              className={styles.Tab}
              classes={{ wrapper: styles.usersTabWrapper }}
              icon={
                <span className={styles.userIcon}>
                  <AccountCircleIcon />
                </span>
              }
              {...a11yProps(3)}
              label={
                <span className={styles.tabLabel}>
                  {totalUsers}
                  {isMinUserModerator && ` (L:${totalLoggedInUsers} G:${totalGuestUsers})`}
                </span>
              }
            />
          )}
          {gameName && !isBroadcasterAsViewer && (
            <Tab
              className={gamesTabClasses}
              icon={<img alt="game icon" src={ThemeService.currentGameTheme.icon} />}
              {...a11yProps(gameName)}
            />
          )}
          {!embedProfileV2 && (
            <Tab
              className={styles.Settings}
              icon={<SettingsIcon />}
              {...a11yProps(5)}
            />
          )}
        </Tabs>
      </AppBar>
      <TabPanel className={styles.TabPanel} index={0} value={profileChatTabValue}>
        <DesktopChatV2
          {...props}
          embedProfileV2={embedProfileV2}
          handleTabsChange={setProfileChatTabValue}
          isGroupShowChat={isGroupShowChat}
          roomName={ProfileStore.profileName}
        />
      </TabPanel>
      {showGSchat && (
        <TabPanel className={styles.TabPanel} index={1} value={profileChatTabValue}>
          <DesktopChatV2
            {...props}
            handleTabsChange={setProfileChatTabValue}
            isGroupShowChat={true}
            roomName={ProfileStore.profileName}
          />
        </TabPanel>
      )}
      {shouldShowPrivateChatInEmbedProfile && !isBroadcasterAsViewer && (
        <TabPanel index={showGSchat ? 2 : 1} value={profileChatTabValue}>
          <DesktopChatV2
            {...props}
            handleTabsChange={setProfileChatTabValue}
            isGroupShowChat={false}
            roomName={ProfileStore.profileName}
          />
        </TabPanel>
      )}
      {!embedProfileV2 && (
        <TabPanel index={determineMultiplePrivateChatIndex()} value={profileChatTabValue}>
          <MultiplePrivateChat {...props} />
        </TabPanel>
      )}
      {!embedProfileV2 && (
        <TabPanel index={determineUserListChatIndex()} value={profileChatTabValue}>
          <UserListTab
            className={styles.UserListTab}
            isRecentSpendingTierLegendOpen={isRecentSpendingTierLegendOpen}
            roomName={ProfileStore.profileName}
            username={user.username}
            onToggleRecentSpendingTierLegend={onToggleRecentSpendingTierLegend}
          />
        </TabPanel>
      )}
      {gameName && (
        <TabPanel index={determineGameTabIndex()} value={profileChatTabValue}>
          <GamesTab {...props} />
        </TabPanel>
      )}
      {!embedProfileV2 && (
        <TabPanel index={determineChatSettingsChatIndex()} value={profileChatTabValue}>
          <ChatSettingsV2
            history={history}
            isDesktop={true}
            isRegisterGoldOverlayVisible={isRegisterGoldOverlayVisible}
            setShowRegistrationModal={setShowRegistrationModal}
            toggleDrawer={noOp}
            toggleRegisterGoldOverlay={toggleRegisterGoldOverlay}
            user={user}
          />
        </TabPanel>
      )}
    </div>
  );
}

ChatTabs.propTypes = {
  appActions: PropTypes.object.isRequired,
  handleGameTab: PropTypes.func.isRequired,
  handlePrivateChat: PropTypes.func.isRequired,
  handleUserList: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  paymentActions: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  history: PropTypes.object,
  isRegisterGoldOverlayVisible: PropTypes.bool,
  totalUsers: PropTypes.number,
  user: PropTypes.object,
  walletDetails: PropTypes.object,
};

ChatTabs.defaultProps = {
  embedProfileV2: false,
  history: undefined,
  isRegisterGoldOverlayVisible: false,
  totalUsers: undefined,
  user: undefined,
  walletDetails: undefined,
};

export default observer(ChatTabs);
